import { Scatter } from "react-chartjs-2";
import { ChartData, ChartOptions } from "chart.js";
import { BirdBowlData } from "../../types/bird/bird-bowl-data";
import { AXIS_LIMITS, GROUND_COLOR_PALETTE } from "./insights-page-utils";

interface Props {
  readonly deliveries: BirdBowlData[];
}

export default function MaxSpinByOverChart({ deliveries }: Props) {
  const grouped: {
    [key: string]: {
      over: number;
      deviations: number[];
      bowlerId: string;
    };
  } = {};

  deliveries.forEach((delivery) => {
    const baseOver = Math.floor((delivery.sequence % 1000000) / 1000);
    const over = delivery.innings === 2 ? baseOver + 20 : baseOver;
    const key = `${delivery.bowlerId.value}_${over}`;
    if (!grouped[key]) {
      grouped[key] = {
        over,
        deviations: [],
        bowlerId: delivery.bowlerName,
      };
    }
    grouped[key].deviations.push(Math.abs(delivery.deviation));
  });

  const datasets: any[] = [];
  const bowlerMap: { [key: string]: { x: number; y: number; stdDev: number }[] } = {};

  Object.values(grouped).forEach(({ over, deviations, bowlerId }) => {
    const max = Math.max(...deviations);
    const mean = deviations.reduce((a, b) => a + b, 0) / deviations.length;
    const variance = deviations.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / deviations.length;
    const stdDev = Math.sqrt(variance);

    if (!bowlerMap[bowlerId]) bowlerMap[bowlerId] = [];
    bowlerMap[bowlerId].push({ x: over, y: max, stdDev });
  });

  Object.entries(bowlerMap).forEach(([bowlerId, points], idx) => {
    datasets.push({
      label: bowlerId,
      data: points.map((p) => ({ x: p.x, y: p.y })),
      showLine: true,
      borderColor: GROUND_COLOR_PALETTE[idx % GROUND_COLOR_PALETTE.length],
      backgroundColor: GROUND_COLOR_PALETTE[idx % GROUND_COLOR_PALETTE.length],
    });
  });

  const overGroups: { [over: number]: number[] } = {};
  deliveries.forEach((delivery) => {
    const baseOver = Math.floor((delivery.sequence % 1000000) / 1000);
    const over = delivery.innings === 2 ? baseOver + 20 : baseOver;
    if (!overGroups[over]) overGroups[over] = [];
    overGroups[over].push(Math.abs(delivery.deviation));
  });

  const stdDevPoints = Object.entries(overGroups).map(([overStr, deviations]) => {
    const over = Number(overStr);
    const mean = deviations.reduce((a, b) => a + b, 0) / deviations.length;
    const variance = deviations.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / deviations.length;
    const stdDev = Math.sqrt(variance);
    return { x: over, y: stdDev };
  });

  datasets.push({
    label: "Standard Deviation",
    data: stdDevPoints,
    type: "bar",
    backgroundColor: "rgba(150, 150, 250, 0.3)",
    borderWidth: 0,
  });

  const chartData: ChartData<"scatter"> = {
    datasets,
  };

  const chartOptions: ChartOptions<"scatter"> = {
    plugins: {
      legend: {
        display: true,
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Over" },
      },
      y: {
        title: { display: true, text: "Max Deviation" },
        min: AXIS_LIMITS["deviation"]?.min,
        max: AXIS_LIMITS["deviation"]?.max,
      },
    },
  };

  return <Scatter data={chartData} options={chartOptions} />;
}